import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut as fbSignOut,
  setPersistence,
  browserLocalPersistence,
  connectAuthEmulator,
  isSignInWithEmailLink,
  signInWithEmailLink,
  type Auth,
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, type Firestore } from 'firebase/firestore';
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage';

const required = (key: keyof ImportMetaEnv): string => {
  const v = import.meta.env[key];
  if (!v) {
    throw new Error(
      `Missing ${key}. Copy .env.example to .env.local and fill in the Firebase web config.`,
    );
  }
  return v;
};

const firebaseConfig = {
  apiKey: required('VITE_FIREBASE_API_KEY'),
  authDomain: required('VITE_FIREBASE_AUTH_DOMAIN'),
  projectId: required('VITE_FIREBASE_PROJECT_ID'),
  storageBucket: required('VITE_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: required('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  appId: required('VITE_FIREBASE_APP_ID'),
};

// Cosmetic Google account-chooser hint only (first allowed domain). NOT a
// security gate — the Functions middleware enforces ALLOWED_DOMAINS server-side.
const ALLOWED_DOMAINS = (import.meta.env.VITE_ALLOWED_DOMAINS || 'henoyo.ai')
  .split(',')
  .map((d) => d.trim())
  .filter(Boolean);

export const app: FirebaseApp = initializeApp(firebaseConfig);
export const auth: Auth = getAuth(app);
export const db: Firestore = getFirestore(app);
export const storage: FirebaseStorage = getStorage(app);

// Local Firebase Emulator Suite. Connect BEFORE any auth/firestore call.
const USE_EMULATORS = import.meta.env.VITE_USE_EMULATORS === 'true';
if (USE_EMULATORS) {
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, 'localhost', 8080);
  connectStorageEmulator(storage, 'localhost', 9199);
}

const provider = new GoogleAuthProvider();
// `hd` is a Google account-chooser hint — UX only, NOT a security gate.
provider.setCustomParameters({ hd: ALLOWED_DOMAINS[0] || 'henoyo.ai', prompt: 'select_account' });

// Persistent session ("stay signed in until logout").
const persistenceReady = setPersistence(auth, browserLocalPersistence).catch(() => {});

export async function signInWithGoogle() {
  await persistenceReady;
  const result = await signInWithPopup(auth, provider);
  return result.user;
}

export async function signOut() {
  await fbSignOut(auth);
}

// ── Magic-link (passwordless) sign-in for invited candidates without Google ───
// The backend (POST /api/request-link) generates the secure link with the Admin
// SDK and emails it — only to INVITED candidate addresses (uniform 200 either way,
// so it never reveals eligibility). The link is completed on this device by
// completeEmailLinkSignIn. Staff use Google, not this path.

const EMAIL_LINK_KEY = 'akhara:emailForSignIn';

/** Request a passwordless sign-in link (uniform success; never reveals eligibility). */
export async function sendSignInLink(email: string): Promise<void> {
  const e = email.trim().toLowerCase();
  const res = await fetch('/api/request-link', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'omit',
    body: JSON.stringify({ email: e }),
  });
  if (!res.ok) throw new Error('Could not send the sign-in link. Please try again.');
  window.localStorage.setItem(EMAIL_LINK_KEY, e);
}

export function isEmailSignInLink(href: string): boolean {
  return isSignInWithEmailLink(auth, href);
}

export function getStoredEmailForSignIn(): string | null {
  return window.localStorage.getItem(EMAIL_LINK_KEY);
}

export function clearStoredEmailForSignIn(): void {
  window.localStorage.removeItem(EMAIL_LINK_KEY);
}

/** Complete a magic-link sign-in. `href` must be the full URL containing the oobCode. */
export async function completeEmailLinkSignIn(email: string, href: string) {
  await persistenceReady;
  const e = email.trim().toLowerCase();
  const result = await signInWithEmailLink(auth, e, href);
  window.localStorage.removeItem(EMAIL_LINK_KEY);
  return result.user;
}
